import React, { useCallback, useEffect, useState } from "react";
import { ArrowDownTrayIcon, DevicePhoneMobileIcon } from "@heroicons/react/24/outline";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { API_URL } from "../config/connect";
import { Card, Empty, ErrorState, Segmented, useChartTheme, PERIODS, fmt } from "./monitoring/ui";

const ANDROID_COLOR = "#22C55E";
const IOS_COLOR = "#3B82F6";

const ymd = (d) => d.toISOString().slice(0, 10);

// Backend әдепкісімен бірдей: бүгінді қоса соңғы N күн.
const rangeFor = (days) => {
  const to = new Date();
  const from = new Date(to.getTime() - (days - 1) * 86400000);
  return { date_from: ymd(from), date_to: ymd(to) };
};

const authHeaders = () => {
  const token = localStorage.getItem("google_id_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const PlatformTotal = ({ label, color, total, period }) => (
  <div className="flex items-center gap-3 rounded-xl border border-border-primary px-4 py-3">
    <span className="h-3 w-3 shrink-0 rounded-full" style={{ backgroundColor: color }} />
    <div className="min-w-0">
      <div className="text-xs text-text-muted">{label}</div>
      <div className="text-xl font-bold text-text-primary">{fmt(total)}</div>
      <div className="text-xs text-text-muted">+{fmt(period)} кезеңде</div>
    </div>
  </div>
);

const InstallsAndReport = () => {
  const [period, setPeriod] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(null);
  const [exportError, setExportError] = useState(null);
  const chart = useChartTheme();

  const fetchInstalls = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const qs = new URLSearchParams(rangeFor(period));
      const res = await fetch(`${API_URL}/api/v1/admin/analytics/installs?${qs}`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setData(await res.json());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    fetchInstalls();
  }, [fetchInstalls]);

  const downloadReport = async (format) => {
    setExporting(format);
    setExportError(null);
    try {
      const range = rangeFor(period);
      const qs = new URLSearchParams({ ...range, format });
      const res = await fetch(`${API_URL}/api/v1/admin/analytics/report?${qs}`, { headers: authHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `agroland_report_${range.date_from}_${range.date_to}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setExportError(e.message);
    } finally {
      setExporting(null);
    }
  };

  const series = (data?.series || []).map((r) => ({ ...r, label: r.date.slice(5).split("-").reverse().join(".") }));
  const hasInstalls = series.some((r) => r.android || r.ios);

  const exportBtn = (format, label) => (
    <button
      type="button"
      onClick={() => downloadReport(format)}
      disabled={!!exporting}
      className="flex items-center gap-1.5 rounded-lg border border-border-primary px-3 py-1.5 text-xs font-semibold text-text-secondary hover:text-text-primary disabled:opacity-50"
    >
      <ArrowDownTrayIcon className={`h-4 w-4 ${exporting === format ? "animate-bounce" : ""}`} />
      {label}
    </button>
  );

  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          <DevicePhoneMobileIcon className="h-4 w-4" /> Орнатулар: Android / iOS
        </span>
      }
      subtitle="Бірегей құрылғылар, бірінші тіркелген күні бойынша"
      actions={
        <div className="flex flex-wrap items-center gap-2">
          <Segmented options={PERIODS} value={period} onChange={setPeriod} />
          {exportBtn("xlsx", "Excel")}
          {exportBtn("docx", "Word")}
        </div>
      }
    >
      {exportError && (
        <div className="mx-5 mt-4 rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-400">
          Отчетті жүктеу қатесі: {exportError}
        </div>
      )}
      {error ? (
        <ErrorState detail={error} onRetry={fetchInstalls} />
      ) : (
        <div className="grid gap-4 p-5 lg:grid-cols-[220px_1fr]">
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-1 lg:content-start">
            <PlatformTotal label="Android" color={ANDROID_COLOR} total={data?.totals?.android} period={data?.period?.android} />
            <PlatformTotal label="iOS" color={IOS_COLOR} total={data?.totals?.ios} period={data?.period?.ios} />
          </div>
          {loading && !data ? (
            <div className="h-72 animate-pulse rounded-xl bg-white/5" />
          ) : hasInstalls ? (
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={series} margin={{ top: 8, right: 16, left: -8, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={chart.grid} vertical={false} />
                  <XAxis dataKey="label" tick={chart.axis} axisLine={false} tickLine={false} minTickGap={12} />
                  <YAxis tick={chart.axis} axisLine={false} tickLine={false} allowDecimals={false} />
                  <Tooltip {...chart.tooltip} formatter={(v, n) => [fmt(v), n]} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="android" name="Android" stackId="p" fill={ANDROID_COLOR} />
                  <Bar dataKey="ios" name="iOS" stackId="p" fill={IOS_COLOR} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <Empty>Бұл кезеңде жаңа орнату жоқ</Empty>
          )}
        </div>
      )}
    </Card>
  );
};

export default InstallsAndReport;
